import type { VenueType } from "./venues";

// Canonical list of F&B business types for the HCMC market. This is the
// single source of truth for the org "Loại hình kinh doanh" field — every
// input surface (employer onboarding, employer org settings, admin org edit)
// must pick from this list rather than free text, so values stay consistent
// across the app. `businessType` is stored and displayed as the label string
// itself (no separate code), so the entries below double as the stored value.
//
// Ordered roughly by how common each format is in HCMC F&B. "Khác" is always
// last — it's the catch-all when nothing else fits (per product: don't offer
// free text, just fall back to "Khác").
export const BUSINESS_TYPES = [
  "Quán cà phê",
  "Cà phê take-away / Xe cà phê",
  "Trà sữa",
  "Quán nước / Giải khát",
  "Tiệm bánh / Bakery",
  "Tiệm kem / Chè / Tráng miệng",
  "Nhà hàng",
  "Quán ăn",
  "Cơm tấm / Cơm văn phòng",
  "Phở / Bún / Hủ tiếu",
  "Bánh mì",
  "Xôi / Cháo / Súp",
  "Hải sản / Ốc",
  "Lẩu / Nướng (BBQ)",
  "Buffet",
  "Quán chay",
  "Đồ ăn Hàn / Nhật / Trung",
  "Pizza / Mì Ý (Âu)",
  "Dimsum / Điểm tâm",
  "Đồ ăn nhanh (Fast food)",
  "Gà rán / Gà ủ muối",
  "Quán ăn vặt / Street food",
  "Quán nhậu / Bia",
  "Bar / Pub / Beer Club",
  "Lounge / Rooftop",
  "Food court / Khu ẩm thực",
  "Căng tin / Bếp ăn công nghiệp",
  "Bếp online (Cloud kitchen)",
  "Khác",
] as const;

export type BusinessType = (typeof BUSINESS_TYPES)[number];

// The catch-all label. Use this as the default/fallback rather than free text.
export const BUSINESS_TYPE_OTHER: BusinessType = "Khác";

// Sensible default for new orgs — cafés dominate the HCMC F&B long tail.
export const DEFAULT_BUSINESS_TYPE: BusinessType = "Quán cà phê";

function isKnownBusinessType(v: string): v is BusinessType {
  return (BUSINESS_TYPES as readonly string[]).includes(v);
}

// Build the option list for a `Select`. If `current` is a value that predates
// this catalog (e.g. legacy free-text orgs, or labels from the old venue-type
// mapping), it's prepended so the field still shows the stored value and the
// edit form never silently drops it — the user can then re-pick a canonical
// option. Pass nothing (or a known/empty value) to get just the catalog.
export function businessTypeOptions(
  current?: string | null,
): { value: string; label: string }[] {
  const opts = BUSINESS_TYPES.map((v) => ({ value: v, label: v }));
  if (current && current.trim() && !isKnownBusinessType(current)) {
    return [{ value: current, label: `${current} (hiện tại)` }, ...opts];
  }
  return opts;
}

// Maps the detailed org `businessType` (29 F&B formats) onto the coarse
// `VenueType` enum (7 values) used by postings — for feed filtering, position-
// group ordering, and title suggestions. The org already captures businessType
// at creation, so postings no longer ask "Loại quán" separately; they derive it
// here. The mapping is a best-effort heuristic; "Khác" / unknown fall back to
// OTHER. Keep in sync with BUSINESS_TYPES above.
export const BUSINESS_TYPE_TO_VENUE: Record<BusinessType, VenueType> = {
  "Quán cà phê": "CAFE",
  "Cà phê take-away / Xe cà phê": "CAFE",
  "Trà sữa": "CAFE",
  "Quán nước / Giải khát": "CAFE",
  "Tiệm bánh / Bakery": "BAKERY",
  "Tiệm kem / Chè / Tráng miệng": "CAFE",
  "Nhà hàng": "RESTAURANT",
  "Quán ăn": "RESTAURANT",
  "Cơm tấm / Cơm văn phòng": "RESTAURANT",
  "Phở / Bún / Hủ tiếu": "RESTAURANT",
  "Bánh mì": "FAST_FOOD",
  "Xôi / Cháo / Súp": "FAST_FOOD",
  "Hải sản / Ốc": "RESTAURANT",
  "Lẩu / Nướng (BBQ)": "HOTPOT_BBQ",
  Buffet: "RESTAURANT",
  "Quán chay": "RESTAURANT",
  "Đồ ăn Hàn / Nhật / Trung": "RESTAURANT",
  "Pizza / Mì Ý (Âu)": "RESTAURANT",
  "Dimsum / Điểm tâm": "RESTAURANT",
  "Đồ ăn nhanh (Fast food)": "FAST_FOOD",
  "Gà rán / Gà ủ muối": "FAST_FOOD",
  "Quán ăn vặt / Street food": "FAST_FOOD",
  "Quán nhậu / Bia": "BAR_PUB",
  "Bar / Pub / Beer Club": "BAR_PUB",
  "Lounge / Rooftop": "BAR_PUB",
  "Food court / Khu ẩm thực": "OTHER",
  "Căng tin / Bếp ăn công nghiệp": "RESTAURANT",
  "Bếp online (Cloud kitchen)": "RESTAURANT",
  Khác: "OTHER",
};

// Derive a posting's `VenueType` from an org's stored `businessType`. Tolerant
// of null/legacy free-text values (falls back to OTHER) so callers can pass
// `org.businessType` straight through.
export function venueTypeFromBusinessType(
  businessType?: string | null,
): VenueType {
  if (businessType && isKnownBusinessType(businessType)) {
    return BUSINESS_TYPE_TO_VENUE[businessType];
  }
  return "OTHER";
}
